// src/services/limuloApi.ts
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getLimuloToken, peekLastSessionFailure, peekLimuloSession } from '@/lib/limulo/session';
import { verdictFor401AfterSessionAttempt } from '@/lib/limulo/errors';
import {
  favoriteGamesOf,
  matchOf,
  matchResultPayloadOf,
  matchesOfParticipant,
  resultMediaAttachOf,
  standingsOf,
  unwrapData,
  unwrapItems,
} from '@/lib/limulo/model';
import type {
  LimuloFavoriteGames,
  LimuloGame,
  LimuloLogoutResult,
  LimuloMatch,
  LimuloMatchResultPayload,
  LimuloMe,
  LimuloPlatform,
  LimuloProfile,
  LimuloRegistrationRequirements,
  LimuloRegistrationResult,
  LimuloRegistrationStatus,
  LimuloResultMediaAttach,
  LimuloTournament,
} from '@/types/limulo';

/**
 * Client del BFF Limulo (`app-qlash-api`) — la TMA e' la PRIMA superficie consumatrice
 * reale di questo backend (ADR-0078). Bersaglio SOLO staging.
 *
 * ⚠️ QUATTRO COSE CHE QUESTO CLIENT NON CONDIVIDE CON GLI ALTRI TREDICI
 *
 * 1. **L'auth non e' Logto.** Il Bearer e' un JWT INTERNO emesso dal BFF in cambio di un
 *    token esterno (`lib/limulo/auth.ts`). `src/lib/logto.ts` non viene toccato: nessuna
 *    resource nuova, nessuno scope nuovo. Se il token non c'e', l'header non si mette —
 *    un `Bearer ` vuoto produrrebbe un 401 che somiglia a un problema di permessi.
 *
 * 2. **L'envelope e' suo.** `{ok, organizationId, data, meta}` e, per le liste,
 *    `data: {items: […]}` con la `pagination` FUORI da `data` (`router.js:73-92`).
 *    `transformResponse` la srotola qui, una volta sola, cosi' le schermate vedono dati
 *    e non buste.
 *
 * 3. 🔴 **`X-AM-ORGANIZATION` SI', e su OGNI rotta — corretto il 2026-09-05 su una misura
 *    che ha smentito la scelta precedente.**
 *
 *    Qui c'era scritto: «l'organizzazione la porta il JWT interno, non mandarlo e' la
 *    scelta piu' sicura». **Falso**, e il costo e' stato una sezione che si apriva e poi
 *    moriva sul dettaglio, con un messaggio che nessuno sapeva leggere.
 *
 *    Misurato su `qlash-limulo@origin/staging`: il BFF ricava l'organizzazione da
 *    `assertUserContext(req)` (`router.js:177-181`), cioe' da cio' che ha messo il
 *    middleware d'auth dell'HOST — e quel middleware la legge **dall'header**. Senza
 *    header, e fuori da una allowlist di sei path (`/auth/me`, `/billing`, …), risponde
 *    **400 «X-AM-ORGANIZATION header is required»** (`middlewares/auth.ts:272-283`).
 *
 *    ⚠️ Il rischio dell'header sbagliato resta vero e non e' stato tolto: un id che non
 *    combacia da' 403 «does not match the active membership». Ma e' un rischio di
 *    VALORE, mentre non mandarlo era un guasto CERTO — e l'errore che si sceglie e'
 *    diverso: 403 dice «org sbagliata», il 400 non diceva niente di utilizzabile.
 *
 *    Si manda solo se c'e' un valore: un header vuoto sarebbe la terza forma di guasto.
 *
 * 4. **Le scritture ci sono dal 2026-09-02 — e la guardia sul retry le copre gia'.**
 *    Fase 1-2 erano di sola lettura; la fase 3 aggiunge iscrizione, referto, prova
 *    allegata, check-in, forfeit, profili e logout (spec:
 *    `docs/analysis/2026-09-02-limulo-collasso-laravel.md` §2). Il `retryCondition` qui
 *    sotto e' scritto sul METODO e non sui nomi delle rotte, quindi nessuna POST viene
 *    mai ritentata: un'iscrizione ripetuta da sola sarebbe una seconda iscrizione.
 *    ⚠️ Sul referto resta il finding aperto lato server (il body non viene spogliato di
 *    `providerResult`): il client non lo nomina, e il gate lo verifica.
 *
 * ⚠️ E UNA COSA CHE VALE PER TUTTE E SETTE: se l'app `qlash-api` non e' attiva
 * sull'organizzazione, ognuna risponde **403 anche con un token valido**. Non e' un
 * guasto da inseguire — e' un interruttore su un backoffice — e le schermate lo dicono
 * invece di mostrare un vuoto.
 * Notare la forma condizionale: qui c'era «con l'app spenta OGGI…», che e' un fatto sul
 * mondo e come tale e' invecchiato in silenzio. Lo stato corrente non si legge dal
 * codice: sta nel KB, `services/qlash-limulo/identita.md`.
 */
/**
 * Il 401 che discende da una NOSTRA scadenza non e' un problema di identita'.
 *
 * 🔴 Percorso misurato il 2026-09-06: l'exchange di sessione scade ⇒ nessun token ⇒ nessun
 * header ⇒ **il server risponde 401 e ha ragione** — quella richiesta non portava credenziali.
 * Ma la schermata leggeva quel 401 come «Identita' non valida — serve una nuova
 * autenticazione», cioe' mandava l'utente a rifare il login (dentro Telegram: il ponte col
 * browser di sistema) per una richiesta che bastava riprovare.
 *
 * ⇒ Qui si ricongiunge cio' che sa `session.ts` (ho rinunciato io) con cio' che vede il
 * client (401). La regola e' pura e sta in `errors.ts`; questo e' solo il punto in cui i due
 * pezzi si incontrano. ⛔ Vale SOLO per la scadenza: un 401 dopo un rifiuto vero resta un
 * rifiuto vero, o si nasconderebbe l'unico caso in cui «rifai l'accesso» e' giusto.
 */
const spiegaIl401 = (
  error: { status?: number | string; data?: unknown } | undefined,
): typeof error => {
  if (!error) return error;
  // ⚠️ `peekLimuloSession()` non tocca la rete: e' la sessione com'e' a riposo. Serve perche'
  // «non abbiamo mandato l'header» spiega un 401 solo se davvero non abbiamo una sessione.
  const causa = verdictFor401AfterSessionAttempt(
    error.status,
    peekLastSessionFailure(),
    peekLimuloSession() !== null,
  );
  if (causa !== 'timeout') return error;
  return { ...error, status: 'TIMEOUT_ERROR' };
};

/**
 * Il client vero, con la coda di ritentativi.
 *
 * ⚠️ Estratto qui, e non lasciato inline, per una ragione di ORDINE: la riscrittura del 401
 * causato dalla nostra scadenza deve avvenire **dopo** i ritentativi. Se avvenisse prima,
 * `retryCondition` vedrebbe `TIMEOUT_ERROR` e **ritenterebbe un 401** — che questo file
 * vieta espressamente due commenti piu' su, e per un buon motivo.
 */
const limuloBaseQuery = retry(
    fetchBaseQuery({
      baseUrl: env.limuloApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getLimuloToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        // Vedi la nota 3: l'host legge l'organizzazione da QUI, non dal JWT interno.
        // Solo se c'e' un valore — `VITE_LIMULO_ORG_ID` non e' dichiarata in ogni
        // profilo, e un header vuoto e' peggio di un header assente.
        if (env.limuloOrgId) headers.set('X-AM-ORGANIZATION', env.limuloOrgId);
        headers.set('Accept', 'application/json');
        return headers;
      },
    }),
    {
      /**
       * Si ritenta solo cio' che puo' cambiare da solo: rete caduta, timeout, 5xx.
       *
       * 401, 403 e **424** non si ritentano MAI, ed e' il 424 il caso che vale la pena
       * spiegare: significa «nessun provider serve questa capability». Riprovare fra un
       * secondo darebbe lo stesso 424, perche' cio' che manca e' una configurazione
       * dell'organizzazione, non un pacchetto perso. Ritentarlo trasformerebbe una
       * risposta onesta e istantanea in tre secondi di rotella seguiti dalla stessa
       * risposta.
       */
      retryCondition: (error, args, { attempt }) => {
        const method =
          typeof args === 'object' && args !== null && 'method' in args
            ? (args as { method?: string }).method
            : undefined;
        if (method !== undefined && method !== 'GET') return false;
        if (attempt > 1) return false;
        const status = (error as { status?: number | string }).status;
        if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
        return typeof status === 'number' && status >= 500;
      },
    },
  );

export const limuloApi = createApi({
  reducerPath: 'limuloApi',
  /**
   * ⛔ La riscrittura sta QUI, dopo i ritentativi, non dentro `fetchBaseQuery`: v. la nota
   * su `limuloBaseQuery`.
   */
  baseQuery: async (args, api, extraOptions) => {
    const result = await limuloBaseQuery(args, api, extraOptions);
    return result.error ? { ...result, error: spiegaIl401(result.error) } : result;
  },
  /**
   * Poll-based PER COSTRUZIONE: il realtime di Limulo e' un guscio (le rotte `/stream`
   * mandano ready + heartbeat e nessun evento di dominio). Non c'e' un push da aspettare,
   * quindi la freschezza la danno il rientro sull'app e la riconnessione.
   */
  keepUnusedDataFor: 60,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: ['LimuloTournament', 'LimuloRegistration', 'LimuloMatch', 'LimuloGame', 'LimuloProfile'],
  endpoints: (builder) => ({
    /**
     * `GET /v1/tournaments` (`router.js:1733-1751`) — lista pubblica, paginata
     * (`page`/`limit`, limite massimo 100 lato server).
     */
    getLimuloTournaments: builder.query<LimuloTournament[], { page?: number; limit?: number } | void>({
      query: (args) => ({
        url: '/v1/tournaments',
        params: { page: args?.page ?? 1, limit: args?.limit ?? 50 },
      }),
      transformResponse: (response: unknown) => unwrapItems<LimuloTournament>(response),
      providesTags: ['LimuloTournament'],
    }),

    /**
     * `GET /v1/tournaments/mine` (`router.js:1753-1777`) — i tornei del chiamante.
     * L'identita' e' il `userId` risolto dal token: la rotta non prende un utente, e non
     * esiste modo di chiedere i tornei di un altro.
     */
    getMyLimuloTournaments: builder.query<LimuloTournament[], void>({
      query: () => '/v1/tournaments/mine',
      transformResponse: (response: unknown) => unwrapItems<LimuloTournament>(response),
      providesTags: ['LimuloTournament'],
    }),

    /** `GET /v1/tournaments/:id` (`router.js:2086-2101`). */
    getLimuloTournament: builder.query<LimuloTournament | null, string>({
      query: (id) => `/v1/tournaments/${encodeURIComponent(id)}`,
      transformResponse: (response: unknown) => unwrapData<LimuloTournament>(response),
      providesTags: (_r, _e, id) => [{ type: 'LimuloTournament' as const, id }],
    }),

    /** `GET /v1/tournaments/:id/registration` — stato d'iscrizione del chiamante. */
    getLimuloRegistration: builder.query<LimuloRegistrationStatus | null, string>({
      query: (id) => `/v1/tournaments/${encodeURIComponent(id)}/registration`,
      transformResponse: (response: unknown) => unwrapData<LimuloRegistrationStatus>(response),
      providesTags: (_r, _e, id) => [{ type: 'LimuloRegistration' as const, id }],
    }),

    /**
     * `GET /v1/tournaments/:id/registration-requirements` — cosa serve per iscriversi.
     * In fase 1 si LEGGE soltanto: l'iscrizione vera e' fase 2.
     */
    getLimuloRegistrationRequirements: builder.query<LimuloRegistrationRequirements | null, string>({
      query: (id) => `/v1/tournaments/${encodeURIComponent(id)}/registration-requirements`,
      transformResponse: (response: unknown) =>
        unwrapData<LimuloRegistrationRequirements>(response),
      providesTags: (_r, _e, id) => [{ type: 'LimuloRegistration' as const, id }],
    }),

    /**
     * `GET /v1/tournaments/:id/standings` — ⚠️ L'UNICA delle sette senza facciata: passa da
     * `handleCapabilityJson` (`router.js:1847-1853`) e restituisce la forma GREZZA del
     * provider. `standingsRows` la normalizza in modo difensivo senza scartare niente, e
     * ogni riga si porta dietro l'originale. Il contratto qui non e' scritto da nessuna
     * parte: ADR-0020 dice che diventera' quello che noi eserciteremo — motivo in piu' per
     * non fingere una forma che il server non ha promesso.
     */
    getLimuloStandings: builder.query<ReturnType<typeof standingsOf>, string>({
      query: (id) => `/v1/tournaments/${encodeURIComponent(id)}/standings`,
      // ⚠️ NON solo le righe. La risposta porta anche `final` e `unavailable`
      // (`classifica.js:185-201`), e buttarli qui significherebbe mostrare `items: []`
      // come «nessun risultato» quando il server ha detto «non disponibile, ed ecco il
      // motivo» — cioe' il finto vuoto che questa sezione esiste per non produrre.
      transformResponse: (response: unknown) => standingsOf(unwrapData(response) ?? response),
      providesTags: (_r, _e, id) => [{ type: 'LimuloTournament' as const, id }],
    }),

    /** `GET /v1/me` (`router.js:825-845`) — utente + membership. Nessuna capability dietro. */
    getLimuloMe: builder.query<LimuloMe | null, void>({
      query: () => '/v1/me',
      transformResponse: (response: unknown) => unwrapData<LimuloMe>(response),
    }),

    // ── Fase 3: le quindici rotte del collasso «sezione Laravel → facciata» ──────
    // Forme dal mirror SERVITO `qlash-limulo@91743eb`. Nessuna schermata le chiama
    // ancora: il cablaggio della UI e' il giro successivo.

    /**
     * `POST /v1/tournaments/:id/registration` (`router.js:1824-1846`, 201).
     *
     * ⚠️ NON si manda `spec.partySize`: la forma della parte la dichiara il TORNEO, e un
     * valore diverso e' un rifiuto nominato (`iscrizioni.js:323-332`). Prima del
     * 2026-08-24 il payload vinceva sul torneo, ed era il varco da cui una parte di
     * gruppo entrava dalla porta dell'individuale.
     *
     * ⚠️ E non esiste `secret_code`: sulla facciata **non c'e' il torneo privato**. Chi
     * porta qui la schermata Laravel deve togliere il foglio password, non tradurlo.
     */
    postLimuloRegistration: builder.mutation<
      LimuloRegistrationResult | null,
      { id: string; name?: string; members?: string[]; profileId?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/registration`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => unwrapData<LimuloRegistrationResult>(response),
      // L'iscrizione cambia `participantsCount`, che la lista mostra: invalidare solo lo
      // stato d'iscrizione lascerebbe a schermo un conteggio vecchio di un posto.
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LimuloRegistration' as const, id },
        { type: 'LimuloTournament' as const, id },
        'LimuloTournament' as const,
      ],
    }),

    /**
     * `GET /v1/tournaments/:id/matches` (`router.js:1855-1880`).
     *
     * ⚠️ IL FILTRO PER PARTECIPANTE NON SI MANDA. `stripMatchClientFields`
     * (`router.js:307-320`) toglie `participantId` dal payload prima che arrivi al
     * provider: la richiesta tornerebbe con TUTTI gli incontri e 200, indistinguibile da
     * un filtro che non ha escluso nessuno. Si chiede la lista e si filtra qui.
     * L'unico filtro che sopravvive e' il round (`incontri.js:289-301`).
     */
    getLimuloMatches: builder.query<
      LimuloMatch[],
      { id: string; round?: number; participantId?: string }
    >({
      query: ({ id, round }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches`,
        params: round === undefined ? undefined : { round },
      }),
      transformResponse: (response: unknown, _meta, { participantId }) =>
        matchesOfParticipant(unwrapItems<unknown>(response).map(matchOf), participantId),
      providesTags: (result) => [
        'LimuloMatch' as const,
        ...(result ?? []).map((m) => ({ type: 'LimuloMatch' as const, id: m.matchId })),
      ],
    }),

    /** `GET /v1/tournaments/:id/matches/:matchId` (`router.js:2066-2085`). */
    getLimuloMatch: builder.query<LimuloMatch | null, { id: string; matchId: string }>({
      query: ({ id, matchId }) =>
        `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}`,
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchOf(data);
      },
      providesTags: (_r, _e, { matchId }) => [{ type: 'LimuloMatch' as const, id: matchId }],
    }),

    /** `GET /v1/tournaments/:id/matches/:matchId/result` (`router.js:1882-1901`). */
    getLimuloMatchResult: builder.query<
      LimuloMatchResultPayload | null,
      { id: string; matchId: string }
    >({
      query: ({ id, matchId }) =>
        `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/result`,
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchResultPayloadOf(data);
      },
      providesTags: (_r, _e, { matchId }) => [{ type: 'LimuloMatch' as const, id: matchId }],
    }),

    /**
     * `POST …/matches/:matchId/result` (`router.js:1903-1922`).
     *
     * ⚠️ Il punteggio e' OBBLIGATORIO e ha una forma sua: `{home, away}` numerici
     * (`incontri.js:626-633`), non la stringa `"3:1"` che il monolite accetta. Il primo
     * invio vince; l'identico e' idempotente; il diverso prende un rifiuto nominato.
     *
     * ⚠️ Nel corpo non entra nient'altro. Il server non spoglia `providerResult` dal body
     * (finding aperto, riportato da qgammon-orc): il client non lo nomina mai.
     */
    postLimuloMatchResult: builder.mutation<
      LimuloMatchResultPayload | null,
      { id: string; matchId: string; score: { home: number; away: number }; externalRef?: string }
    >({
      query: ({ id, matchId, score, externalRef }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/result`,
        method: 'POST',
        body: { score, ...(externalRef === undefined ? {} : { externalRef }) },
      }),
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchResultPayloadOf(data);
      },
      // Un referto muove la classifica del torneo, non solo la riga dell'incontro.
      invalidatesTags: (_r, _e, { id, matchId }) => [
        { type: 'LimuloMatch' as const, id: matchId },
        'LimuloMatch' as const,
        { type: 'LimuloTournament' as const, id },
      ],
    }),

    /**
     * `POST …/matches/:matchId/result-media` (`router.js:1924-1983`) — multipart.
     *
     * ⚠️ E' il SECONDO passo, non un'alternativa al referto: il server registra l'asset
     * (`media.asset.register`, app `media`) e poi lo allega. Sul monolite l'immagine si
     * postava sullo STESSO `/result` del punteggio (`endpoints/matches.ts:40-53`): qui
     * prima si manda `score`, poi la prova.
     *
     * ⚠️ Nessun `Content-Type`: lo scrive il browser col boundary, e forzarlo rompe
     * l'upload. Il campo si chiama `file` (`router.js:762-764`), le immagini ammesse sono
     * jpeg/png/webp/gif fino a 15 MB (`:59-66`).
     *
     * ⚠️ Se l'app `media` non e' attiva sull'organizzazione, questa rotta risponde **424**
     * (nessun provider per la capability), non 403. Lo stato dell'app NON si legge da qui:
     * e' configurazione e cambia senza preavviso — la fonte viva, col grado di ogni riga,
     * e' `services/qlash-limulo/identita.md` nel KB. Qui c'era «su staging e' spenta»,
     * cioe' un fatto sul mondo scritto al presente: la stessa forma che su `qlash-api` e'
     * rimasta falsa per tre giorni senza che niente lo segnalasse.
     */
    postLimuloMatchResultMedia: builder.mutation<
      LimuloResultMediaAttach | null,
      { id: string; matchId: string; file: File; clientMutationId?: string }
    >({
      query: ({ id, matchId, file, clientMutationId }) => {
        const form = new FormData();
        form.append('file', file);
        if (clientMutationId !== undefined) form.append('clientMutationId', clientMutationId);
        return {
          url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/result-media`,
          method: 'POST',
          body: form,
        };
      },
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : resultMediaAttachOf(data);
      },
      invalidatesTags: (_r, _e, { matchId }) => [{ type: 'LimuloMatch' as const, id: matchId }],
    }),

    /**
     * `POST …/matches/:matchId/checkin` (`router.js:1985-2004`), corpo vuoto.
     * Ripetuto e' `gia-registrato`, non un errore (`catalogo.js:193`).
     *
     * ⚠️ Questo e' il check-in dell'INCONTRO. Il check-in di TORNEO del monolite
     * (`PUT /tournaments/:id/participants`) non esiste sulla facciata ed e' stato
     * rinunciato come prodotto (L-88, decisione dell'operatore del 2026-09-02).
     */
    postLimuloMatchCheckin: builder.mutation<
      LimuloMatchResultPayload | null,
      { id: string; matchId: string }
    >({
      query: ({ id, matchId }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/checkin`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchResultPayloadOf(data);
      },
      // Una presenza non tocca la classifica: si invalida l'incontro e basta.
      invalidatesTags: (_r, _e, { matchId }) => [{ type: 'LimuloMatch' as const, id: matchId }],
    }),

    /**
     * `POST …/matches/:matchId/forfeit` (`router.js:2006-2025`).
     *
     * ⚠️ `reason` e' OBBLIGATORIO (`incontri.js:1112-1117`, ADR-0019): «un forfeit senza
     * motivo e' una decisione arbitrale che nessuno potra' piu' spiegare». Il corpo vuoto
     * che manda il monolite (`endpoints/matches.ts:77-87`) qui e' un 4xx nominato — la UI
     * dovra' chiedere il motivo, non solo confermare.
     */
    postLimuloMatchForfeit: builder.mutation<
      LimuloMatchResultPayload | null,
      { id: string; matchId: string; reason: string }
    >({
      query: ({ id, matchId, reason }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/forfeit`,
        method: 'POST',
        body: { reason },
      }),
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchResultPayloadOf(data);
      },
      // Un forfeit e' un referto marcato: stessa invalidazione del referto.
      invalidatesTags: (_r, _e, { id, matchId }) => [
        { type: 'LimuloMatch' as const, id: matchId },
        'LimuloMatch' as const,
        { type: 'LimuloTournament' as const, id },
      ],
    }),

    /**
     * `GET /v1/games` (`router.js:1644-1660`), provider `app-games@220`.
     *
     * ⚠️ NON serve a risolvere il `gameId` di un torneo in un nome: vincolo kb#81, il
     * gioco si mostra in passthrough. Serve a chi deve ELENCARE i giochi.
     * ⚠️ E `id` qui e' un ObjectId, non l'id numerico del monolite (`CR_GAME_ID = 6`):
     * il selettore di gioco della TMA non riconoscerebbe nessuna di queste righe.
     */
    getLimuloGames: builder.query<LimuloGame[], void>({
      query: () => '/v1/games',
      transformResponse: (response: unknown) => unwrapItems<LimuloGame>(response),
      providesTags: ['LimuloGame'],
    }),

    /** `GET /v1/platforms` (`router.js:1626-1642`). */
    getLimuloPlatforms: builder.query<LimuloPlatform[], void>({
      query: () => '/v1/platforms',
      transformResponse: (response: unknown) => unwrapItems<LimuloPlatform>(response),
      providesTags: ['LimuloGame'],
    }),

    /**
     * `GET /v1/me/favorite-games` (`router.js:1689`).
     *
     * ⚠️ RISPONDE UN OGGETTO, non una lista: `sanitizeFavoriteGameResult`
     * (`policy.js:178-187`) dentro `sendOk` (`router.js:1683-1685`). `unwrapItems` qui
     * darebbe `[]` con 200 — «nessun preferito» dove la verita' e' un'altra forma.
     * L'alias `/v1/users/:userId/favourite-games` (`:1690`) non si usa: accetta solo il
     * proprio id (`:1671-1674`), quindi non aggiunge niente e ne chiede uno in piu'.
     */
    getLimuloFavoriteGames: builder.query<LimuloFavoriteGames, void>({
      query: () => '/v1/me/favorite-games',
      transformResponse: (response: unknown) => favoriteGamesOf(unwrapData(response)),
      providesTags: ['LimuloGame'],
    }),

    /**
     * `GET /v1/games/:gameId/profile-requirements` (`router.js:2218-2235`) — che cosa serve
     * per creare il profilo di gioco di UN gioco.
     *
     * 🔴 **E' l'unica rotta di questa sezione SENZA facciata**: l'handler fa
     * `sendOk(res, req, data)` sul dato grezzo del provider, senza `sanitize*`. La forma e'
     * quindi al 100% di `readGameProfileRequirements`
     * (`app-games/runtime-services.js:1781-1830`) e puo' cambiare senza che il BFF cambi —
     * stessa classe di `standings`. Per questo il tipo qui e' `unknown` e la lettura la fa
     * `profileFormPlan` (`lib/limulo/registration.ts`) campo per campo: dichiarare
     * un'interfaccia darebbe a un contratto non promesso la faccia di uno promesso.
     *
     * ⚠️ Il `:gameId` e' il CODICE del gioco (`tournament.gameId`, passthrough kb#81), come
     * per ogni altra giunzione di questa sezione — mai un ObjectId.
     */
    getLimuloGameProfileRequirements: builder.query<unknown, string>({
      query: (gameId) => `/v1/games/${encodeURIComponent(gameId)}/profile-requirements`,
      transformResponse: (response: unknown) => unwrapData<unknown>(response),
      // Dipende dai profili dell'utente (`status` ci finisce dentro), non solo dal gioco:
      // creato un profilo, i requisiti letti prima non descrivono piu' la stessa situazione.
      providesTags: ['LimuloProfile', 'LimuloGame'],
    }),

    /**
     * `GET /v1/games/profiles` (`router.js:2105-2124`) — i profili del CHIAMANTE.
     * Il `:id` del path Laravel sparisce: l'utente lo mette il server dal JWT (`:2113`).
     */
    getLimuloProfiles: builder.query<LimuloProfile[], { activeOnly?: boolean } | void>({
      query: (args) => ({
        url: '/v1/games/profiles',
        params: args && args.activeOnly !== undefined ? { activeOnly: args.activeOnly } : undefined,
      }),
      transformResponse: (response: unknown) => unwrapItems<LimuloProfile>(response),
      providesTags: ['LimuloProfile'],
    }),

    /**
     * `GET /v1/games/profiles/:profileId` (`router.js:2126-2140`).
     * Un profilo di un altro utente risponde 403 (`assertProfileBelongsToUser`, `:302-309`).
     */
    getLimuloProfile: builder.query<LimuloProfile | null, string>({
      query: (profileId) => `/v1/games/profiles/${encodeURIComponent(profileId)}`,
      transformResponse: (response: unknown) => unwrapData<LimuloProfile>(response),
      providesTags: (_r, _e, profileId) => [{ type: 'LimuloProfile' as const, id: profileId }],
    }),

    /**
     * `POST /v1/games/profiles` (`router.js:2142-2156`, 201).
     *
     * ⚠️ I NOMI SONO QUELLI DEL PROVIDER, non del monolite: `gameId` (codice, non il 6
     * numerico), `platformId`, `externalPlayerId` — dove Laravel manda
     * `{game_id, platform_id, info: {id, nickname}}` (`endpoints/games.ts:43-50`).
     * Letti su `app-games/runtime-services.js:556+` (`normalizePlayerPayload`).
     *
     * ⚠️ E l'upsert bulk del monolite qui NON esiste: sono due rotte, e «crea o aggiorna»
     * diventa «leggi la lista, poi POST oppure PATCH».
     */
    createLimuloProfile: builder.mutation<
      LimuloProfile | null,
      /**
       * ⚠️ L'argomento e' APERTO di proposito, e prima era un letterale a cinque chiavi che
       * costringeva il chiamante a un `as never` — cioe' a spegnere il compilatore su un
       * contratto falso.
       *
       * I campi veri li dichiara il SERVER, gioco per gioco
       * (`GET /v1/games/:gameId/profile-requirements`): per Clash Royale sono `playerTag` e
       * `apiToken`, che qui non c'erano. E non sono nemmeno colonne del profilo —
       * `normalizeProviderPayload` (`app-games/runtime-services.js:249-271`) instrada le
       * chiavi di `PROVIDER_PAYLOAD_FIELDS` dentro `meta.provider`. Elencarle qui
       * significherebbe ricopiare uno schema che cambia con i giochi, in un posto che nessun
       * test lega alla fonte.
       * ⇒ `gameId` e' l'unica chiave che il BFF pretende sempre; il resto lo compone
       * `profileFormBody` (`lib/limulo/registration.ts`) dai campi dichiarati dal server.
       * ⛔ `userId` non entra mai: lo mette il server (`buildSafeProfilePayload`).
       */
      { gameId: string } & Record<string, string>
    >({
      query: (body) => ({ url: '/v1/games/profiles', method: 'POST', body }),
      transformResponse: (response: unknown) => unwrapData<LimuloProfile>(response),
      invalidatesTags: ['LimuloProfile'],
    }),

    /** `PATCH /v1/games/profiles/:profileId` (`router.js:2158-2181`). */
    updateLimuloProfile: builder.mutation<
      LimuloProfile | null,
      {
        profileId: string;
        platformId?: string;
        externalPlayerId?: string;
        displayName?: string;
        handle?: string;
      }
    >({
      query: ({ profileId, ...body }) => ({
        url: `/v1/games/profiles/${encodeURIComponent(profileId)}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) => unwrapData<LimuloProfile>(response),
      invalidatesTags: (_r, _e, { profileId }) => [
        { type: 'LimuloProfile' as const, id: profileId },
        'LimuloProfile' as const,
      ],
    }),

    /**
     * `POST /v1/auth/logout` (`router.js:711-723`).
     *
     * ⚠️ Il `refreshToken` va nel CORPO (`mobile-auth.js:905`): senza, il server prova a
     * risalire alla sessione dal Bearer, e se il token e' gia' scaduto non revoca niente.
     *
     * ⚠️ NESSUN TAG: questa mutation non invalida cache, la BUTTA — ma svuotare lo store
     * e' compito di chi gestisce la sessione, non di una query. Oggi `endLimuloSession()`
     * (`lib/limulo/session.ts:62-64`) cancella solo il `localStorage` e la sessione resta
     * VIVA sul server fino a scadenza: il cablaggio e' in APERTO, perche' tocca
     * `session.ts`, fuori dal perimetro di questo giro.
     */
    postLimuloLogout: builder.mutation<LimuloLogoutResult | null, { refreshToken: string }>({
      query: ({ refreshToken }) => ({
        url: '/v1/auth/logout',
        method: 'POST',
        body: { refreshToken },
      }),
      transformResponse: (response: unknown) => unwrapData<LimuloLogoutResult>(response),
    }),
  }),
});

export const {
  useGetLimuloTournamentsQuery,
  useGetMyLimuloTournamentsQuery,
  useGetLimuloTournamentQuery,
  useGetLimuloRegistrationQuery,
  useGetLimuloRegistrationRequirementsQuery,
  useGetLimuloStandingsQuery,
  useGetLimuloMeQuery,
  // Fase 3 — nessuna schermata li usa ancora: il cablaggio e' il giro successivo.
  usePostLimuloRegistrationMutation,
  useGetLimuloMatchesQuery,
  useGetLimuloMatchQuery,
  useGetLimuloMatchResultQuery,
  // ⚠️ Le due varianti LAZY servono alla RILETTURA dopo un rifiuto di scrittura, non a una
  // schermata: il BFF appiattisce ogni rifiuto nominato del provider in un 424 muto (v. il
  // cappello di `lib/limulo/matchActions.ts`), e l'unico modo di sapere com'e' andata e'
  // guardare cosa il server HA. Deve partire SU RICHIESTA, dopo il rifiuto — un `useQuery`
  // al montaggio leggerebbe lo stato di prima.
  useLazyGetLimuloMatchQuery,
  useLazyGetLimuloMatchResultQuery,
  usePostLimuloMatchResultMutation,
  usePostLimuloMatchResultMediaMutation,
  usePostLimuloMatchCheckinMutation,
  usePostLimuloMatchForfeitMutation,
  useGetLimuloGamesQuery,
  useGetLimuloPlatformsQuery,
  useGetLimuloFavoriteGamesQuery,
  useGetLimuloGameProfileRequirementsQuery,
  useGetLimuloProfilesQuery,
  // La rilettura dopo un rifiuto di scrittura, stessa ragione delle due lazy qui sopra.
  useLazyGetLimuloProfilesQuery,
  useLazyGetLimuloRegistrationQuery,
  useGetLimuloProfileQuery,
  useCreateLimuloProfileMutation,
  useUpdateLimuloProfileMutation,
  usePostLimuloLogoutMutation,
} = limuloApi;
